import { Check, Circle, X } from 'lucide-react';

import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { SubsetDeniedAlert } from '@/features/agents/components/SubsetDeniedAlert';

import type { AttachItemState } from '../types';

export interface AttachProgressListProps {
  items: AttachItemState[];
  onRetry: (itemId: string) => void;
  /** Shown on the policies item when its failure is a subset denial. */
  onEditGrants?: () => void;
}

function StatusIcon({ status }: { status: AttachItemState['status'] }) {
  if (status === 'done') {
    return <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />;
  }
  if (status === 'error') {
    return <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />;
  }
  if (status === 'running') {
    return <LoadingSpinner size="sm" label="In progress" className="mt-0.5 shrink-0" />;
  }
  return <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />;
}

/**
 * Post-create deferred-attach sequence (design §15.3) — `role="status"`
 * `aria-live="polite"` so every state change is announced. One line per item
 * (✓ / ⟳ / ✕ / ○) with a one-line outcome and inline, per-item retry; a 403
 * subset denial renders the exact (action, resource) pairs with the
 * escalate/edit resolutions inline (§13.3) — never only a toast.
 */
export function AttachProgressList({ items, onRetry, onEditGrants }: AttachProgressListProps) {
  return (
    <ul role="status" aria-live="polite" aria-label="Setting up your agent" className="space-y-3">
      {items.map((item) => {
        const isPoliciesDenial = item.denial !== undefined && item.id.endsWith(':policies');
        return (
          <li key={item.id} className="flex items-start gap-2">
            <StatusIcon status={item.status} />
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm">
                <span className="font-medium">{item.label}</span>
                {item.outcome && item.status !== 'error' ? (
                  <span className="text-muted-foreground"> — {item.outcome}</span>
                ) : null}
                {item.status === 'pending' ? (
                  <span className="text-muted-foreground">
                    {' '}
                    — {item.id.endsWith(':triggers') ? 'queued' : 'waiting'}
                  </span>
                ) : null}
              </p>

              {item.status === 'error' && item.denial ? (
                <SubsetDeniedAlert
                  denial={item.denial}
                  title={`Couldn't grant the requested permissions for “${item.label}”.`}
                  intro="You don't hold these permissions yourself:"
                  resolution="Remove the grant and retry, or ask an administrator to grant them to you."
                  className="text-sm"
                />
              ) : item.status === 'error' && item.outcome ? (
                <p className="text-sm text-muted-foreground">{item.outcome}</p>
              ) : null}

              {item.status === 'error' ? (
                <div className="flex gap-2">
                  {isPoliciesDenial && onEditGrants ? (
                    <Button type="button" variant="outline" size="sm" onClick={onEditGrants}>
                      Edit grants
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onRetry(item.id)}
                  >
                    Retry
                  </Button>
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
